import { useState } from 'react'
import { Tooltip } from 'antd'
import { formatDollars } from './budgetCalculatorModel'

type DonutSegment = {
  label: string
  value: number
  color: string
}

type Props = {
  data: DonutSegment[]
  centerText?: string
  centerValue?: string
}

const BudgetDonutChart = ({ data, centerText = '', centerValue = '' }: Props) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const centerX = 150
  const centerY = 150
  const outerRadius = 120
  const innerRadius = 80

  const total = data.reduce((sum, segment) => sum + segment.value, 0)

  const generateArcPath = (startAngle: number, endAngle: number): string => {
    const x1 = centerX + outerRadius * Math.cos(startAngle)
    const y1 = centerY + outerRadius * Math.sin(startAngle)
    const x2 = centerX + outerRadius * Math.cos(endAngle)
    const y2 = centerY + outerRadius * Math.sin(endAngle)
    const x3 = centerX + innerRadius * Math.cos(endAngle)
    const y3 = centerY + innerRadius * Math.sin(endAngle)
    const x4 = centerX + innerRadius * Math.cos(startAngle)
    const y4 = centerY + innerRadius * Math.sin(startAngle)

    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0

    return `
      M ${x1} ${y1}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}
      Z
    `
  }

  let currentAngle = -Math.PI / 2
  const segments = data.map((segment, index) => {
    const segmentAngle = (segment.value / total) * 2 * Math.PI
    const startAngle = currentAngle
    const endAngle = currentAngle + segmentAngle
    currentAngle = endAngle

    return {
      ...segment,
      path: generateArcPath(startAngle, endAngle),
      index
    }
  })

  return (
    <svg width="300" height="300" viewBox="0 0 300 300">
      {segments.map((segment) => (
        <Tooltip
          key={segment.index}
          title={`${segment.label}: ${formatDollars(segment.value)}`}
        >
          <path
            d={segment.path}
            fill={segment.color}
            opacity={hoveredIndex === null || hoveredIndex === segment.index ? 1 : 0.6}
            onMouseEnter={() => setHoveredIndex(segment.index)}
            onMouseLeave={() => setHoveredIndex(null)}
            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
          />
        </Tooltip>
      ))}

      <text
        x={centerX}
        y={centerY - 15}
        textAnchor="middle"
        fill="#666"
        fontSize="14"
        fontFamily="sans-serif"
      >
        {centerText}
      </text>

      <text
        x={centerX}
        y={centerY + 10}
        textAnchor="middle"
        fill="#000"
        fontSize="24"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        {centerValue}
      </text>
    </svg>
  )
}

export default BudgetDonutChart
